(function () {
  if (!window.customElements || customElements.get("begin-explore-products"))
    return;

  const DEFAULT_MOBILE_BREAKPOINT = 1410;

  class BeginExploreProducts extends HTMLElement {
    static get observedAttributes() {
      return ["active-tab"];
    }

    constructor() {
      super();
      this._initialized = false;
      this._desktop = {
        tabs: [],
        wrappers: [],
        panels: [],
        panelMap: {},
      };
      this._mobile = {
        root: null,
        tabs: [],
        sliderEl: null,
        slides: [],
        swiper: null,
        navPrev: null,
        navNext: null,
      };
      this._onHashChange = this._onHashChange.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._root = this.classList.contains("explore-products")
        ? this
        : this.querySelector(".explore-products") || this;
      if (!this.classList.contains("explore-products"))
        this.classList.add("explore-products");

      this.dataset.epEnhanced = "true";
      this._setupDesktop();
      if (this.dataset.layout === "dual") {
        this._setupMobile();
      }
      this._initialActivation();
      window.addEventListener("hashchange", this._onHashChange);
      this.dispatchEvent(
        new CustomEvent("ep:mount", {
          bubbles: true,
          detail: { instance: this },
        })
      );
    }

    disconnectedCallback() {
      window.removeEventListener("hashchange", this._onHashChange);
      this._destroyMobile();
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (!this._initialized) return;
      if (name === "active-tab" && oldVal !== newVal) {
        const idx = parseInt(newVal, 10) - 1;
        if (!isNaN(idx))
          this.selectTab(idx, { updateHash: false, focus: false });
      }
    }

    selectTab(indexOrId, opts = {}) {
      const { updateHash = true, focus = true, source = "api" } = opts;
      let tab = null;
      if (typeof indexOrId === "number") {
        tab = this._desktop.tabs[indexOrId];
      } else if (typeof indexOrId === "string") {
        tab = this._desktop.tabs.find(
          (t) =>
            t.id === indexOrId ||
            t.getAttribute("data-tab-target") === indexOrId
        );
      } else if (indexOrId instanceof HTMLElement) {
        tab = indexOrId;
      }
      if (!tab) return false;
      this._setActiveDesktopTab(tab, { updateHash, focus, source });
      return true;
    }
    next() {
      const i = this._activeIndex();
      if (i === -1) return false;
      const next = (i + 1) % this._desktop.tabs.length;
      return this.selectTab(next);
    }
    prev() {
      const i = this._activeIndex();
      if (i === -1) return false;
      const prev =
        (i - 1 + this._desktop.tabs.length) % this._desktop.tabs.length;
      return this.selectTab(prev);
    }
    refresh() {
      this._setupDesktop(true);
      if (this.dataset.layout === "dual") {
        this._setupMobile(true);
      }
      this._initialActivation();
    }

    _setupDesktop(force = false) {
      if (this._desktop.initialized && !force) return;
      const container =
        this.dataset.layout === "dual"
          ? this.querySelector(
              '.explore-products__tabs-outer[data-version="desktop"]'
            )
          : this._root;
      this._desktop.tabs = container
        ? Array.from(container.querySelectorAll(".explore-products__tab"))
        : [];
      this._desktop.wrappers = container
        ? Array.from(
            container.querySelectorAll(".explore-products__tab-wrapper")
          )
        : [];
      this._desktop.panels = Array.from(
        this._root.querySelectorAll(
          '.explore-products__panels[data-version="desktop"] .explore-products__panel'
        )
      );
      this._desktop.panelMap = this._desktop.panels.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {});

      this._desktop.tabs.forEach((t) => {
        if (!t.classList.contains("is-active")) t.tabIndex = -1;
      });

      this._desktop.tabs.forEach((tab) => {
        tab.__epClick && tab.removeEventListener("click", tab.__epClick);
        tab.__epKey && tab.removeEventListener("keydown", tab.__epKey);
        tab.__epClick = () => this._setActiveDesktopTab(tab);
        tab.__epKey = (e) => this._onTabKeydown(e, tab);
        tab.addEventListener("click", tab.__epClick);
        tab.addEventListener("keydown", tab.__epKey);
      });
      this._desktop.wrappers.forEach((w) => {
        w.__epClick && w.removeEventListener("click", w.__epClick);
        w.__epClick = () => {
          const btn = w.querySelector(".explore-products__tab");
          if (btn && !btn.classList.contains("is-active"))
            this._setActiveDesktopTab(btn);
        };
        w.addEventListener("click", w.__epClick);
      });
      this._desktop.initialized = true;
    }

    _onTabKeydown(e, tab) {
      const tabs = this._desktop.tabs;
      const idx = tabs.indexOf(tab);
      let nextIdx = null;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIdx = (idx + 1) % tabs.length;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIdx = (idx - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIdx = 0;
          break;
        case "End":
          nextIdx = tabs.length - 1;
          break;
        case "Enter":
        case " ":
          this._setActiveDesktopTab(tab);
          return;
      }
      if (nextIdx !== null) {
        e.preventDefault();
        this._setActiveDesktopTab(tabs[nextIdx]);
      }
    }

    _setActiveDesktopTab(
      tab,
      { updateHash = true, focus = true, source = "user" } = {}
    ) {
      if (!tab) return;
      const targetId = tab.getAttribute("data-tab-target");
      const panel = this._desktop.panelMap[targetId];
      if (!targetId || !panel) return;
      const { tabs, panels, wrappers } = this._desktop;
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle("is-active", active);
        t.setAttribute("aria-selected", active ? "true" : "false");
        t.tabIndex = active ? 0 : -1;
      });
      panels.forEach((p) => {
        const active = p === panel;
        p.classList.toggle("is-active", active);
        if (active) {
          p.removeAttribute("hidden");
        } else {
          p.setAttribute("hidden", "");
        }
      });
      wrappers.forEach((w) =>
        w.classList.toggle("is-active", !!w.contains(tab))
      );
      const idx = tabs.indexOf(tab);
      this.setAttribute("active-tab", String(idx + 1));
      if (updateHash && tab.id && history.replaceState) {
        history.replaceState(null, "", "#" + tab.id);
      }
      if (focus) tab.focus();
      this.dispatchEvent(
        new CustomEvent("ep:change", {
          bubbles: true,
          detail: { index: idx, tab, panel, source },
        })
      );
    }

    _activeIndex() {
      return this._desktop.tabs.findIndex((t) =>
        t.classList.contains("is-active")
      );
    }

    _initialActivation() {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const match = this._desktop.tabs.find((t) => t.id === hash);
        if (match) {
          this._setActiveDesktopTab(match, {
            updateHash: false,
            focus: false,
            source: "hash",
          });
          return;
        }
      }
      const pre = this._desktop.tabs.find((t) =>
        t.classList.contains("is-active")
      );
      if (pre) {
        this._setActiveDesktopTab(pre, {
          updateHash: false,
          focus: false,
          source: "markup",
        });
        return;
      }
      if (this._desktop.tabs.length) {
        this._setActiveDesktopTab(this._desktop.tabs[0], {
          updateHash: false,
          focus: false,
          source: "auto",
        });
      }
    }

    _onHashChange() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const match = this._desktop.tabs.find((t) => t.id === hash);
      if (match) this._setActiveDesktopTab(match, { updateHash: false });
    }

    _setupMobile(force = false) {
      if (this._mobile.initialized && !force) return;
      const mobileRoot = this._root.querySelector('[data-version="mobile"]');
      if (!mobileRoot) {
        return;
      }
      this._mobile.root = mobileRoot;
      this._mobile.sliderEl = mobileRoot.querySelector(
        ".explore-products__mobile-slider"
      );
      this._mobile.tabs = Array.from(
        mobileRoot.querySelectorAll(".explore-products__mobile-tab")
      );
      this._mobile.slides = this._mobile.sliderEl
        ? Array.from(this._mobile.sliderEl.querySelectorAll(".swiper-slide"))
        : [];
      this._mobile.navPrev = this._mobile.sliderEl
        ? this._mobile.sliderEl.querySelector(
            ".explore-products__nav-btn--prev"
          )
        : null;
      this._mobile.navNext = this._mobile.sliderEl
        ? this._mobile.sliderEl.querySelector(
            ".explore-products__nav-btn--next"
          )
        : null;
      this._initMobileLogic();
      this._mobile.initialized = true;
    }

    _initMobileLogic() {
      const m = this._mobile;
      const MM = window.matchMedia
        ? window.matchMedia(`(max-width: ${this._mobileBreakpoint()}px)`)
        : null;
      const isMobileView = () =>
        MM ? MM.matches : window.innerWidth <= this._mobileBreakpoint();

      const sync = (idx) => {
        m.tabs.forEach((t, i) => {
          const active = i === idx;
          t.classList.toggle("is-active", active);
          if (active) t.setAttribute("aria-current", "true");
          else t.removeAttribute("aria-current");
        });
        m.slides.forEach((s, i) => {
          const active = i === idx;
          s.classList.toggle("is-active", active);
          if (active) s.removeAttribute("aria-hidden");
          else s.setAttribute("aria-hidden", "true");
        });
      };

      const fallbackActivate = () => {
        m.root.hidden = false;
        sync(0);
        m.tabs.forEach((tab) => {
          tab.__epMobClick &&
            tab.removeEventListener("click", tab.__epMobClick);
          tab.__epMobClick = () => {
            const idx = Number(tab.dataset.mobileIndex || 0);
            sync(idx);
          };
          tab.addEventListener("click", tab.__epMobClick);
        });
      };

      const initSwiper = () => {
        if (!m.sliderEl) return;
        if (!isMobileView()) {
          destroySwiper();
          m.root.hidden = true;
          return;
        }
        m.root.hidden = false;
        if (m.swiper || !window.Swiper) return;
        m.swiper = new Swiper(m.sliderEl, {
          slidesPerView: 1,
          loop: true,
          centeredSlides: true,
          centerInsufficientSlides: true,
          spaceBetween: 0,
          autoHeight: true,
          navigation:
            m.navNext && m.navPrev
              ? { nextEl: m.navNext, prevEl: m.navPrev }
              : undefined,
          on: {
            init() {
              sync(this.realIndex || 0);
            },
            slideChange() {
              sync(this.realIndex);
            },
          },
        });
      };

      const destroySwiper = () => {
        if (m.swiper) {
          try {
            m.swiper.destroy(true, true);
          } catch (e) {}
          m.swiper = null;
        }
      };

      if (window.Swiper) {
        initSwiper();
      } else {
        let attempts = 0;
        const max = 20;
        const int = setInterval(() => {
          if (window.Swiper) {
            clearInterval(int);
            initSwiper();
          } else if (++attempts >= max) {
            clearInterval(int);
            fallbackActivate();
          }
        }, 100);
      }

      if (MM) {
        MM.addEventListener("change", () => {
          if (isMobileView()) {
            initSwiper();
          } else {
            destroySwiper();
            m.root.hidden = true;
          }
        });
      } else {
        window.addEventListener("resize", () => {
          if (isMobileView()) {
            initSwiper();
          } else {
            destroySwiper();
            m.root.hidden = true;
          }
        });
      }
    }

    _destroyMobile() {
      const m = this._mobile;
      if (m && m.swiper) {
        try {
          m.swiper.destroy(true, true);
        } catch (e) {}
        m.swiper = null;
      }
    }

    _mobileBreakpoint() {
      const attr = this.getAttribute("mobile-breakpoint");
      const v = parseInt(attr, 10);
      return !isNaN(v) ? v : DEFAULT_MOBILE_BREAKPOINT;
    }
  }

  customElements.define("begin-explore-products", BeginExploreProducts);
})();
